import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useNavigate } from "react-router-dom";
import { subscribe_newsletter } from "../../api/Enquiry";
import { toast } from "react-toastify";
import { useSettings } from "../../context/Settings";
import { assetUrl } from "../../utils/baseUrls";

export default function Footer() {
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const logoUrl = settings?.data?.logo
    ? assetUrl(settings.data.logo)
    : "/assets/images/logo.png";

  const handleSubscribe = async () => {
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      await subscribe_newsletter({ email });
      toast.success("Subscribed to Newsletter!");
      setEmail("");
    } catch (err) {
      toast.error("Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="footer" sx={{ bgcolor: "#FFFFFF", pt: 6, pb: 4, borderTop: "1px solid #EAEAEA" }}>
      <Container maxWidth="xl">
        <Grid container spacing={4} justifyContent="space-between">
          {/* Column 1: Brand & Description */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Geo Farms Logo"
                sx={{ height: 50, width: "auto", objectFit: "contain" }}
              />
            </Box>
            <Typography sx={{ fontSize: "0.82rem", color: "#555", lineHeight: 1.6, pr: { md: 4 } }}>
              Geo-Farms Provides services for property buying, selling, and rental assistance. The company helps clients find suitable residential and commercial properties, including houses, plots, shops, offices, and farmland.
            </Typography>
          </Grid>

          {/* Column 2: Quick Links */}
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#13381E", mb: 2 }}>
              Links
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                onClick={() => navigate("/contact-us")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Contact Us
              </Typography>
              <Typography
                onClick={() => navigate("/pages/about_us")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                About Us
              </Typography>
              <Typography
                onClick={() => navigate("/material-store")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Material Store
              </Typography>
              <Typography
                onClick={() => navigate("/professional-services")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Services
              </Typography>
            </Box>
          </Grid>

          {/* Column 3: Legal */}
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#13381E", mb: 2 }}>
              Legal
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                onClick={() => navigate("/pages/terms_condition")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Terms & Conditions
              </Typography>
              <Typography
                onClick={() => navigate("/pages/privacy_policy")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Privacy Policy
              </Typography>
              <Typography
                onClick={() => navigate("/pages/disclaimer_footer")}
                sx={{ fontSize: "0.82rem", color: "#444", cursor: "pointer", "&:hover": { color: "#59B400" } }}
              >
                Disclaimer
              </Typography>
            </Box>
          </Grid>

          {/* Column 4: News Letter & Social Icons */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "0.95rem", color: "#13381E", mb: 1.5 }}>
              News Letter
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#666", mb: 2 }}>
              Subscribe to our news channel to get latest updates
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <TextField
                placeholder="Your Email Address"
                variant="outlined"
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                sx={{
                  bgcolor: "#FAFAFA",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
                  "& input": { fontSize: "0.8rem" },
                }}
              />
              <Button
                variant="contained"
                onClick={handleSubscribe}
                disabled={loading}
                sx={{
                  bgcolor: "#163F1D",
                  color: "#FFFFFF",
                  borderRadius: "10px",
                  px: 3,
                  fontWeight: 800,
                  fontSize: "0.82rem",
                  textTransform: "none",
                  boxShadow: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { bgcolor: "#0F2F14" },
                }}
              >
                {loading ? <CircularProgress size={18} color="inherit" /> : "Subscribe"}
              </Button>
            </Box>

            {/* Social Icons */}
            <Typography sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#13381E", mb: 1 }}>
              Follow Us
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <IconButton
                onClick={() => window.open(settings?.data?.youtube || "https://youtube.com", "_blank")}
                sx={{ bgcolor: "#163F1D", color: "#FFFFFF", "&:hover": { bgcolor: "#0F2F14" }, width: 36, height: 36 }}
              >
                <YouTubeIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
              <IconButton
                onClick={() => window.open(`https://wa.me/${settings?.data?.whatsapp || ""}`, "_blank")}
                sx={{ bgcolor: "#163F1D", color: "#FFFFFF", "&:hover": { bgcolor: "#0F2F14" }, width: 36, height: 36 }}
              >
                <WhatsAppIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
              <IconButton
                onClick={() => window.open(settings?.data?.instagram || "https://instagram.com", "_blank")}
                sx={{ bgcolor: "#163F1D", color: "#FFFFFF", "&:hover": { bgcolor: "#0F2F14" }, width: 36, height: 36 }}
              >
                <InstagramIcon sx={{ fontSize: "1.1rem" }} />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Bottom Copyright Bar */}
        <Box sx={{ mt: 5, pt: 2, borderTop: "1px solid #F0F4F1", textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.75rem", color: "#888" }}>
            © 2026 Geo Rental Farms. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
